import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Save, LogOut } from 'lucide-react';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const DEFAULT_PRODUCTS = [
  {
    id: 'free',
    name: 'Free',
    badge: '',
    features: 'Basic system tweaks\nCommunity access\nDiscord support',
  },
  {
    id: 'vantatweak_v1',
    name: 'VantaTweak V1',
    badge: 'Popular',
    features: '25+ optimized tweaks\nFPS boost package\nInstant download\nEmail support',
  },
  {
    id: 'vantatweak_v2',
    name: 'VantaTweak V2',
    badge: 'Best Value',
    features: '50+ premium tweaks\nMacro suite included\nPriority support\nLifetime updates\nExclusive Discord role',
  },
];

const loadProducts = () => {
  try {
    const stored = localStorage.getItem('vanta_admin_products');
    return stored ? JSON.parse(stored) : DEFAULT_PRODUCTS;
  } catch {
    return DEFAULT_PRODUCTS;
  }
};

export default function AdminPanel() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState(loadProducts);
  const [saved, setSaved] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password && password === ADMIN_PASSWORD) {
      setLoggedIn(true);
      setError('');
    } else {
      setError(t.admin.wrongPass);
    }
  };

  const updateProduct = (id, field, value) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem('vanta_admin_products', JSON.stringify(products));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <h1 className="font-outfit text-3xl font-light text-white text-center mb-2">{t.admin.title}</h1>
          <p className="font-inter text-sm text-zinc-600 text-center mb-10">Vanta Admin Access</p>

          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder={t.admin.passLabel}
              className="w-full px-5 py-3.5 rounded-2xl border border-white/15 bg-white/[0.03] text-white font-inter text-sm placeholder:text-zinc-600 focus:outline-none focus:border-white/40 transition-colors backdrop-blur-xl"
            />
            {error && (
              <p data-testid="admin-error" className="text-red-400 text-sm font-inter text-center">{error}</p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full py-3.5 bg-white text-black text-sm font-medium font-inter rounded-2xl hover:bg-zinc-200 transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
            >
              {t.admin.enterBtn}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full flex items-center justify-center gap-2 text-zinc-600 hover:text-white text-sm font-inter transition-colors"
          >
            <ArrowLeft size={14} /> Back to site
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-6 py-16">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="font-outfit text-3xl font-light text-white">{t.admin.title}</h1>
            <p className="font-inter text-sm text-zinc-600 mt-1">Manage product display</p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-save-btn"
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-black text-sm font-medium font-inter rounded-full hover:bg-zinc-200 transition-all"
            >
              <Save size={14} />
              {saved ? t.admin.saved : t.admin.saveBtn}
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={() => setLoggedIn(false)}
              className="flex items-center gap-2 px-5 py-2.5 border border-white/15 text-zinc-400 hover:text-white text-sm font-inter rounded-full transition-all hover:border-white/30"
            >
              <LogOut size={14} />
              {t.admin.logout}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {products.map((product) => (
            <motion.div
              key={product.id}
              data-testid={`admin-product-${product.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
            >
              <p className="font-inter text-xs tracking-widest text-zinc-600 uppercase mb-5">{product.id}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-inter text-xs text-zinc-500 mb-2">{t.admin.productName}</label>
                  <input
                    data-testid={`admin-name-${product.id}`}
                    type="text"
                    value={product.name}
                    onChange={e => updateProduct(product.id, 'name', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] text-white font-inter text-sm focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block font-inter text-xs text-zinc-500 mb-2">Badge</label>
                  <input
                    data-testid={`admin-badge-${product.id}`}
                    type="text"
                    value={product.badge}
                    onChange={e => updateProduct(product.id, 'badge', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] text-white font-inter text-sm focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-inter text-xs text-zinc-500 mb-2">{t.admin.productFeatures}</label>
                  <textarea
                    data-testid={`admin-features-${product.id}`}
                    value={product.features}
                    onChange={e => updateProduct(product.id, 'features', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] text-white font-inter text-sm focus:outline-none focus:border-white/30 transition-colors resize-none"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
